import { ReactJSXElement } from '@emotion/react/types/jsx-namespace'
import CircularProgress from '@mui/material/CircularProgress'
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { getAvailableCalendar } from '../customer-information/api'
import Day from './Day'
import styles from './styles.module.css'
import DrivingDetails from './DrivingDetails'
import { fetchData } from '../../../../../global/helper-functions/fetch'
import { MODES } from '../../../../../global/constants/scheduler'
import { IInstallationPoint } from '../../../../../../../app/entities/InstallationPoint'
import { WoDetailContext } from '../../../../../work-orders/components/context/context'

interface IAppointmentSlotsProps {
  selectedTimes: number[]
  setSelectedTimes: Function
  setDisabled: Function
  ref: any
  setInstallationPoint: Function
  mode: string
  chosenAddress?: google.maps.GeocoderResult | null | undefined
  installationPoint?: IInstallationPoint
  orderObjectId?: string
  locationId: string
}

//TODO rework interfaces to match DayAvailability type
export const AppointmentSlots = forwardRef(
  (
    {
      selectedTimes,
      setSelectedTimes,
      chosenAddress,
      setDisabled,
      setInstallationPoint,
      installationPoint,
      mode,
      orderObjectId,
      locationId,
    }: IAppointmentSlotsProps,
    ref,
  ) => {
    const [days, setDays] = useState([])

    const [isLoading, setLoading] = useState(false)
    const [nextIsLoading] = useState(false)

    const { data: order } = useContext(WoDetailContext)

    useImperativeHandle(
      ref,
      () => ({
        async useNextClick(callback: Function) {
          callback(true)
        },
        async useSaveClick() {
          if (!orderObjectId)
            return alert(
              'Could not save appointment, refresh page and try again.',
            )

          try {
            await fetchData<any>({
              method: 'PUT',
              url: `${process.env.REACT_APP_ORIGIN}/orders/${orderObjectId}/route-instruction/from-time`,
              body: {
                selectedTimes,
              },
            })
          } catch (err) {
            console.error(err)
            alert(err)
          }

          // if order is cancelled and we are rescheduling the appointment time, we can assume we are trying to get
          // this order back on the schedule, so we need to change the status back to scheduled
          if (['cancelled_onsite', 'cancelled'].includes(order?.status || '')) {
            try {
              await fetchData<any>({
                method: 'PUT',
                url: `${process.env.REACT_APP_ORIGIN}/orders/${orderObjectId}/status/scheduled`,
                body: {
                  selectedTimes,
                },
              })
            } catch (err) {
              console.error(err)
              alert(err)
            }
          }
        },
      }),
      [selectedTimes, orderObjectId],
    )

    //TODO add type to getAvailableCalendar

    const zipCode =
      mode === MODES.EDIT
        ? installationPoint?.zipCode ?? ''
        : chosenAddress?.address_components.find((component) =>
            component.types.includes('postal_code'),
          )?.long_name || ''

    useEffect(() => {
      async function runFunc() {
        try {
          setLoading(true)
          const res = await fetchData<any>(
            getAvailableCalendar(zipCode, locationId),
          )
          setDays(res)
          setLoading(false)
        } catch (err) {
          console.error(err)
          alert(err)
        }
      }

      runFunc()
    }, [])

    const onSetSelected = (selectedTimes: number[]) => {
      setSelectedTimes(selectedTimes)
    }

    useEffect(() => {
      setDisabled(selectedTimes?.length === 0)
    }, [selectedTimes])

    const daysJsx: Array<ReactJSXElement> = days.map((day, idx) => (
      <Day
        setSelected={onSetSelected}
        selectedTimes={selectedTimes}
        day={day}
        key={idx}
      />
    ))

    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      )
    }

    return (
      <div className={styles.appointmentSlotsContainer}>
        <DrivingDetails
          chosenAddress={chosenAddress || null}
          installationPoint={installationPoint}
          setInstallationPoint={setInstallationPoint}
        />
        {nextIsLoading && (
          <div className={styles.overlay}>
            <div className={styles.progressContainer}>
              <CircularProgress size={80} />
            </div>
          </div>
        )}
        <div className={styles.slotsContainer}>
          <div className={styles.daysContainer}>{daysJsx}</div>
        </div>
      </div>
    )
  },
)
